import { Suspense, type ReactNode } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router";
import { Toaster } from "sonner";
import { ToasterErrorBoundary } from "@/components/common/ToasterErrorBoundary";
import { NewVersionBanner } from "@/components/common/NewVersionBanner";
import { EnvironmentBanner } from "@/components/environment/EnvironmentBanner";
import { environmentMarkLabel } from "@/lib/environment";
import { ENV_TOASTER_OFFSETS } from "@/lib/environmentMarkStyles";
import { PageLoader } from "@/components/common/PageLoader";
import { RouteErrorBoundary } from "@/components/common/RouteErrorBoundary";
import { lazyPage } from "@/lib/lazyPage";

// Pages in the entry bundle: the public entry points (sign-in, password reset,
// consultant signup, cookie settings) and the 404, so an applicant opening the
// signup link never waits for a second request.
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import NotFoundPage from "./pages/NotFoundPage";
import PublicConsultantSignupPage from "./pages/PublicConsultantSignupPage";
import PrivacySettingsPage from "./pages/PrivacySettingsPage";

// Context providers
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ConsentProvider } from "./contexts/ConsentContext";

// Layout
import ProtectedRoute from "./components/layout/ProtectedRoute";
import AdminRoute from "./components/auth/AdminRoute";
import { CompanyManagerRoute } from "./components/auth/CompanyManagerRoute";
import { ComplianceRoute } from "./components/policies/ComplianceRoute";
import {
  AdminPoliciesRedirect,
  LegacyComplianceRedirect,
  PoliciesRedirect,
} from "./components/compliance/LegacyPolicyRedirects";
import { PolicyGateGuard, POLICY_GATE_PATH } from "./components/policies/PolicyGateGuard";
import { AdminLayout } from "./components/layout/AdminLayout";
import { AppLayout } from "./components/layout/AppLayout";

// Analytics
import Analytics from "./components/analytics/Analytics";
import ConsentBanner from "./components/analytics/ConsentBanner";

// Signed-in pages load their code on first visit (#995): consultants never
// download the admin pages, and applicants download neither.
const DashboardPage = lazyPage(() => import("./pages/DashboardPage"));
const AssignmentsPage = lazyPage(() => import("./pages/AssignmentsPage"));
const AssignmentDetailPage = lazyPage(() => import("./pages/AssignmentDetailPage"));
const SkillsPage = lazyPage(() => import("./pages/SkillsPage"));
const FinancialsPage = lazyPage(() => import("./pages/FinancialsPage"));
const ConsultantEngagementsPage = lazyPage(() => import("./pages/ConsultantEngagementsPage"));
const TimeTrackingPage = lazyPage(() => import("./pages/TimeTrackingPage"));
const HourStatisticsPage = lazyPage(() => import("./pages/HourStatisticsPage"));
const ProfilePage = lazyPage(() => import("./pages/ProfilePage"));
const SettingsPage = lazyPage(() => import("./pages/SettingsPage"));
const MyCompanyPage = lazyPage(() => import("./pages/my-company/MyCompanyPage"));
const TeamMemberPage = lazyPage(() => import("./pages/my-company/TeamMemberPage"));
const ComplianceTaskPage = lazyPage(() => import("./pages/compliance/ComplianceTaskPage"));
const AgreementChoicePage = lazyPage(() => import("./pages/compliance/AgreementChoicePage"));
const PolicyGatePage = lazyPage(() => import("./pages/PolicyGatePage"));

// Admin
const ConsultantApplicationsPage = lazyPage(
  () => import("./pages/admin/ConsultantApplicationsPage")
);
const ConsultantApplicationDetailPage = lazyPage(
  () => import("./pages/admin/ConsultantApplicationDetailPage")
);
const AdminConsultantsListPage = lazyPage(() => import("./pages/admin/consultants/ListPage"));
const AdminConsultantDetailPage = lazyPage(() => import("./pages/admin/consultants/DetailPage"));
const AdminConsultantEditPage = lazyPage(() => import("./pages/admin/consultants/EditPage"));
const AdminSkillsPage = lazyPage(() => import("./pages/admin/AdminSkillsPage"));
const AdminTimesheetsPage = lazyPage(() => import("./pages/admin/timesheets/AdminTimesheetsPage"));
const AdminTimesheetDetailPage = lazyPage(
  () => import("./pages/admin/timesheets/AdminTimesheetDetailPage")
);
const AdminCompaniesListPage = lazyPage(() => import("./pages/admin/companies/ListPage"));
const AdminCompanyDetailPage = lazyPage(() => import("./pages/admin/companies/DetailPage"));
const AdminCompanyEditPage = lazyPage(() => import("./pages/admin/companies/EditPage"));
const AdminCompanyNewPage = lazyPage(() => import("./pages/admin/companies/NewPage"));
const AdminAssignmentsListPage = lazyPage(() => import("./pages/admin/assignments/ListPage"));
const AdminAssignmentEditPage = lazyPage(() => import("./pages/admin/assignments/EditPage"));
const AdminAssignmentWorkflowPage = lazyPage(
  () => import("./pages/admin/assignments/WorkflowPage")
);
const PermissionsPage = lazyPage(() => import("./pages/admin/permissions/PermissionsPage"));
const ConfigPage = lazyPage(() => import("./pages/admin/config/ConfigPage"));
const AdminFinancialsPage = lazyPage(() => import("./pages/admin/financials/AdminFinancialsPage"));
const ErrorLogsPage = lazyPage(() => import("./pages/admin/error-logs/ErrorLogsPage"));
const AdminCompliancePage = lazyPage(() => import("./pages/admin/compliance/AdminCompliancePage"));

/**
 * Wraps all routes. The layouts have their own boundary and loader; this one
 * covers the routes outside them: a lazy page there (the policy gate) shows
 * the full-screen loader while its code loads, and a chunk that fails to load
 * self-heals or shows the error fallback instead of a white screen (#917).
 */
const RouteArea = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen bg-background">
      <RouteErrorBoundary resetKey={pathname}>
        <Suspense fallback={<PageLoader fullScreen />}>{children}</Suspense>
      </RouteErrorBoundary>
    </div>
  );
};

// Create query client with enhanced configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

// Toasts clear the non-production banner (#1099); PROD keeps sonner's defaults.
const toasterOffsets = environmentMarkLabel() ? ENV_TOASTER_OFFSETS : {};

// Main App component with properly nested providers
const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <ConsentProvider>
            <BrowserRouter>
              <TooltipProvider>
                {/* First in the flow: the DEV strip on every page (#1099). */}
                <EnvironmentBanner />
                <Analytics />
                <ConsentBanner />
                <NewVersionBanner />
                <RouteArea>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/consultant-signup" element={<PublicConsultantSignupPage />} />
                    <Route path="/privacy-settings" element={<PrivacySettingsPage />} />

                    {/* Admin routes */}
                    <Route
                      path="/admin"
                      element={
                        <AdminRoute>
                          <AdminLayout />
                        </AdminRoute>
                      }
                    >
                      <Route index element={<Navigate to="/admin/consultants" replace />} />
                      <Route path="consultants">
                        <Route index element={<AdminConsultantsListPage />} />
                        <Route path="applications" element={<ConsultantApplicationsPage />} />
                        <Route
                          path="applications/:id"
                          element={<ConsultantApplicationDetailPage />}
                        />
                        <Route path=":id" element={<AdminConsultantDetailPage />} />
                        <Route path=":id/edit" element={<AdminConsultantEditPage />} />
                      </Route>
                      <Route path="companies">
                        <Route index element={<AdminCompaniesListPage />} />
                        <Route path="new" element={<AdminCompanyNewPage />} />
                        <Route path=":id" element={<AdminCompanyDetailPage />} />
                        <Route path=":id/edit" element={<AdminCompanyEditPage />} />
                      </Route>
                      <Route path="assignments" element={<AdminAssignmentsListPage />} />
                      <Route path="assignments/:id" element={<AdminAssignmentWorkflowPage />} />
                      <Route path="assignments/:id/edit" element={<AdminAssignmentEditPage />} />
                      <Route path="skills" element={<AdminSkillsPage />} />
                      <Route path="timesheets" element={<AdminTimesheetsPage />} />
                      <Route path="timesheets/:id" element={<AdminTimesheetDetailPage />} />
                      <Route path="permissions" element={<PermissionsPage />} />
                      <Route path="financials" element={<AdminFinancialsPage />} />
                      <Route path="config" element={<ConfigPage />} />
                      <Route path="error-logs" element={<ErrorLogsPage />} />
                      {/* Renamed from Policies (backend#894); old links keep working. */}
                      <Route
                        path="compliance"
                        element={
                          <ComplianceRoute>
                            <AdminCompliancePage />
                          </ComplianceRoute>
                        }
                      />
                      <Route
                        path="policies"
                        element={
                          <ComplianceRoute>
                            <AdminPoliciesRedirect />
                          </ComplianceRoute>
                        }
                      />
                    </Route>

                    {/* Blocking policy acknowledgement (BE #864): outside the
                        gated layout so it stays reachable while policies are
                        pending. */}
                    <Route
                      path={POLICY_GATE_PATH}
                      element={
                        <ProtectedRoute>
                          <ComplianceRoute>
                            <PolicyGatePage />
                          </ComplianceRoute>
                        </ProtectedRoute>
                      }
                    />

                    {/* Protected routes with layout */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <PolicyGateGuard>
                            <AppLayout />
                          </PolicyGateGuard>
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<DashboardPage />} />
                      <Route path="dashboard" element={<Navigate to="/" replace />} />
                      <Route path="assignments" element={<AssignmentsPage />} />
                      <Route path="assignments/:id" element={<AssignmentDetailPage />} />
                      <Route path="skills" element={<SkillsPage />} />
                      {/* Employees of a consultancy company see no invoices
                          (backend#1069). */}
                      <Route
                        path="financials"
                        element={
                          <CompanyManagerRoute>
                            <FinancialsPage />
                          </CompanyManagerRoute>
                        }
                      />
                      <Route
                        path="consultant-engagements"
                        element={<ConsultantEngagementsPage />}
                      />
                      <Route path="time-tracking" element={<TimeTrackingPage />} />
                      <Route path="hour-statistics" element={<HourStatisticsPage />} />
                      {/* Renamed from Hour Reporting (#941); old links and
                          notification action URLs keep working. */}
                      <Route
                        path="hour-reporting"
                        element={<Navigate to="/hour-statistics" replace />}
                      />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="settings" element={<SettingsPage />} />
                      {/* My Company (#1132), before that Compliance (backend#894)
                          and Policies. The request email links to a task; old
                          /compliance/* and /policies links keep working. */}
                      <Route
                        path="my-company"
                        element={
                          <ComplianceRoute>
                            <MyCompanyPage />
                          </ComplianceRoute>
                        }
                      />
                      {/* A manager editing a colleague from the Team tab (#1133). */}
                      <Route
                        path="my-company/team/:userId"
                        element={
                          <ComplianceRoute>
                            <TeamMemberPage />
                          </ComplianceRoute>
                        }
                      />
                      <Route
                        path="my-company/tasks/:taskId"
                        element={
                          <ComplianceRoute>
                            <ComplianceTaskPage />
                          </ComplianceRoute>
                        }
                      />
                      {/* The price option choice email links here (backend#865). */}
                      <Route
                        path="my-company/agreements/:agreementId"
                        element={
                          <ComplianceRoute>
                            <AgreementChoicePage />
                          </ComplianceRoute>
                        }
                      />
                      <Route path="compliance/*" element={<LegacyComplianceRedirect />} />
                      <Route
                        path="policies"
                        element={
                          <ComplianceRoute>
                            <PoliciesRedirect />
                          </ComplianceRoute>
                        }
                      />
                    </Route>

                    {/* 404 page */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </RouteArea>
                <ToasterErrorBoundary>
                  <Toaster position="top-right" {...toasterOffsets} />
                </ToasterErrorBoundary>
              </TooltipProvider>
            </BrowserRouter>
          </ConsentProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
